import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Button, Tooltip, TextField, FormControlLabel, Switch, Snackbar, CircularProgress, LinearProgress } from '@mui/material';
import Alert from '@mui/material/Alert';
import { Delete, Visibility } from '@mui/icons-material';
import { createServiceBusClient, createMessageReceiverAsync } from "../modules/createServiceBusClient"
import { createEvent, createErrorEvent, createMessageEvent } from "../modules/logManager.js";

const maxNumberOfMessages = 500;



const Root = styled('div')(({ theme }) => ({
  // root styles
}));

const Form = styled('form')(({ theme }) => ({
  // form styles
}));

const FormItem = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

// const InlineInput = styled('div')(({ theme }) => ({
//   marginRight: theme.spacing(2),
// }));

const ButtonWrapper = styled('div')({
  display: "inline-block",
  padding: "0px",
  margin: "0px"
});

const ProgressContainer = styled('div')(({ theme }) => ({
  marginLeft: theme.spacing(0.5),
  marginTop: theme.spacing(2),
}));

// function Alert(props) {
//   return <MuiAlert elevation={6} variant="filled" {...props} />;
// }


  let refreshInterval = null;

  export default function ReceiveMessages(props) {
    const {setErrorMessage } = props;
    const [isLoading, setIsLoading] = useState(false);
    const [continuousMode, setContinuousMode] = useState(false);
    const [subscription, setSubscription] = useState(null);
    const [fromDLQ, setFromDLQ] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [numberOfMessages, setNumberOfMessages] = useState(1);
    const { receiveMode = "peekLock" } = props;
    const [receiveResult, setReceiveResult] = useState("Received messages successfully");
    const [openNotification, setOpenNotification] = useState(false);
    const continuousModeBtnLabelPrefix = continuousMode ? subscription !== null ? "Stop " : "Start " : "";
    const continuousModeRefreshInterval = 5000; // refresh every 5 seconds

    useEffect(() => {
      if(props.selectedEntity && !props.selectedEntity.requiresSession){
        setSessionId(null);
      }
    }
    , [ props.selectedEntity ]);


    const handleNotificationClose = (event, reason) => {
      setOpenNotification(false);
    };

    // a function that refreshes the current selected entity if it supports refreshing i.e. to update count of active messages upon receiving a message
    const refresh = () => {
      if(props.selectedEntity && props.selectedEntity.refresh)
      {
        props.selectedEntity.refresh();
      }
    }
    
    const event = Object.assign({}, 
      createEvent(props.entityInfo, receiveMode), 
      fromDLQ ? {
        entityName: `${props.entityInfo.entityName}/$deadLetterQueue`
        }: {}
    );

    const getMessages = async () => {
      window.dataLayer && window.dataLayer.push({
        event: receiveMode === 'receiveAndDelete' ? 'receive_messages' : 'peek_messages',
        number_of_messages: numberOfMessages,
        continuous_mode: continuousMode,
      });
      // if there is an active subscription this will just clean up / stop and return
      if(receiveMode === "receiveAndDelete" && subscription && subscription.close)
      {
        await subscription.close();
        await subscription.receiver.close();
        await subscription.serviceBusClient.close();
        setIsLoading(false);
        setSubscription(null);
        if(refreshInterval !== null)
        {
          clearInterval(refreshInterval);
          refreshInterval = null;
        }
        setReceiveResult("Stopped receiving messages")
        setOpenNotification(true);
        return;
      }
      

      // start loading indicator
      setIsLoading(true);

      // callback to run when message is received by a message receiver
      // if receiveMode is peekLock, the handler also abandon the message
      const onMessageReceived = (receiver) => async (message) => {
        if(receiveMode === "peekLock") { 
          try {
              await receiver.abandonMessage(message);
          } catch (error) {
            props.onMessageEventReceived(createErrorEvent(Object.assign({}, event, {operation: "abandon"}), props.entityInfo, error, { message }, props.selectedEntity));
          }
        } 
        props.onMessageEventReceived(createMessageEvent(event, message));
      };

      // callback to run when any error occurs while any receive operation is going
      const onReceiveError = (error) => {
        setErrorMessage(`Error receiving messages: ${(error.errors && error.errors.length > 0 ? error.errors[error.errors.length - 1].toString() : '') || error.message || (error.toString ? error.toString() : '') || JSON.stringify(error)}`);
        props.onMessageEventReceived(createErrorEvent(event, props.entityInfo, error, { fromDLQ, sessionId }, props.selectedEntity));
      };

      // create client
      const serviceBusClient = createServiceBusClient(props.entityInfo, props.credentials, 3);
      const receiverOptions = Object.assign({
        receiveMode,
        maxWaitTimeInMs: 3600000 // Message read timeout 60 minutes
      },
      sessionId ? {
        sessionId,
      }: {},
      fromDLQ  ? {
        subQueueType: "deadLetter"
      } : {});

      try {
        // create receiver
        const receiver = await createMessageReceiverAsync(serviceBusClient, props.entityInfo, receiverOptions, sessionId);
        // start receiving using through continuous mode, then exit to wait for a stop receiving signal
        if(continuousMode)
        {
          setSubscription(Object.assign({}, {
            serviceBusClient,
            receiver
          }, receiver.subscribe({ 
            processMessage: onMessageReceived(receiver), 
            processError: onReceiveError, 
          }, {
            maxConcurrentCalls: 5
          })));
          if(refreshInterval != null)
          {
            clearInterval(refreshInterval);
            refreshInterval = null;
          }
          refreshInterval = setInterval(refresh, continuousModeRefreshInterval);
          setReceiveResult("Started receiving messages")
          setOpenNotification(true);
          return;
        } 
        // receive specific number of messages
        const receivedMessages = await receiver.receiveMessages(numberOfMessages < 1 ? 1 : numberOfMessages, {
          maxWaitTimeInMs: 4000,
        });
        // no messages
        if(receivedMessages.length === 0)
        {
          setReceiveResult("No messages available")
          setOpenNotification(true);
          props.onMessageEventReceived(Object.assign({}, event, {
            time: new Date(),
          }));
        } 
        else {
          // received messages
          setReceiveResult(`Successfully received ${receivedMessages.length} messages.`)
          setOpenNotification(true);
          // process received messages
          await Promise.all(receivedMessages.map(onMessageReceived(receiver)));
        }
        // trigger refresh for UI to reflect number of active messages after receive
        refresh();
        // cleanup after receive specific number of messages
        setIsLoading(false);
        await receiver.close();
        await serviceBusClient.close();
      }
      catch (error) {
        onReceiveError(error);
        setIsLoading(false);
        await serviceBusClient.close();
      }
  }
    return (
      <Root>
          <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} open={openNotification} autoHideDuration={16000} onClose={handleNotificationClose}>
            <Alert onClose={handleNotificationClose} severity="success" variant="filled">
              {receiveResult}
            </Alert>
          </Snackbar>
          <Form noValidate autoComplete="off">
            { continuousMode ? null :
                <FormItem>
              <TextField
                  size="small"
                  variant="outlined"
                  margin="dense"
                  disabled={!!subscription}
                  label="Number of messages to retrieve"
                  value={numberOfMessages}
                  onChange={(e) => {setNumberOfMessages(Number(e.target.value) > maxNumberOfMessages ? maxNumberOfMessages : (Number(e.target.value) < 0 ? 1 : Number(e.target.value)))}}
                  InputProps={{ inputProps: { min: 1, max: maxNumberOfMessages } }}
                  type="number"
                  fullWidth
                />
              </FormItem>
              }
             { (props.selectedEntity && !props.selectedEntity.requiresSession) || fromDLQ ? null :
              <FormItem>
              <TextField
                  size="small"
                  variant="outlined"
                  margin="dense"
                  label="Session Id"
                  value={sessionId || ""}
                  onChange={(e) => {setSessionId(e.target.value.length > 0 ? e.target.value : null)}}
                  fullWidth
                />
              </FormItem>
              } 
            <FormItem>
                <FormControlLabel
                  control={
                    <Switch
                      checked={fromDLQ}
                      onChange={(e) => {setFromDLQ(e.target.checked )}}
                      name="fromDLQ"
                      disabled={!!subscription}
                      color="primary"
                    />
                  }
                  label="From Dead Letter Queue"
                />
              </FormItem>
              {
                receiveMode !== "peekLock" ?
                <FormItem>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={continuousMode}
                        onChange={(e) => {setContinuousMode(e.target.checked )}}
                        name="continuousMode"
                        disabled={!!subscription}
                        color="primary"
                      />
                    }
                    label="Continuous (Purge)"
                  />
                </FormItem>
                : null
              }
              <FormItem>
              <Tooltip title={props.entityInfo.isValid ? "" : "Please make sure you fill entity information details correctly."} placement="right">
                <ButtonWrapper>
                <Button onClick={getMessages} disabled={(isLoading || (!props.entityInfo.isValid)) && !subscription} variant="contained" color="primary"  endIcon={receiveMode === "peekLock" ? <Visibility /> : <Delete />}>
                    {receiveMode === "peekLock" ? `${continuousModeBtnLabelPrefix}Peek` : `${continuousModeBtnLabelPrefix}Receive and Delete`}
                  </Button>
                </ButtonWrapper>
              </Tooltip>
              </FormItem>
              {isLoading 
              ? <ProgressContainer>
                  {
                    continuousMode ?
                      <LinearProgress />
                    : <CircularProgress color="secondary" />
                  }
              </ProgressContainer>
              : undefined  
              }
          </Form>
      </Root>
    );
  }

  export { maxNumberOfMessages, ReceiveMessages };